import { useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import SharedAsset from '@zenbusiness/common-components/SharedAsset';
import PropTypes from 'prop-types';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import VideoContentView from '#client/components/VideoCard/VideoContentView';

import { MT_DATA_TRACKING } from '../../utils/constants';
import content from './content.json';

const StyledCard = styled(Card)(({ theme }) => ({
  width: '256px',
  height: '236px',
  borderRadius: '12px',
  border: `0.5px solid ${theme.palette.focus40.main}`,

  [theme.breakpoints.up('md')]: {
    width: '400px',
    height: '344px'
  }
}));

const StledCardActionArea = styled(CardActionArea)(({
  display: 'flex',
  flexDirection: 'column',

  justifyContent: 'flex-start'
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: '135px',
  [theme.breakpoints.up('md')]: {
    height: '212px'
  }
}));

const CardBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',

  padding: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  gap: theme.spacing(0.5),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    gap: theme.spacing(1.5)
  }
}));

const CardTitle = styled(Typography)`
${({ theme }) => ({
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    width: '100%',
    ...theme.typography.body_largeBold
  })};
`;

const TimeStamp = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  flexDirection: 'row',
  ...theme.typography.caption_largeRegular,

  paddingTop: theme.spacing(2)
}));

const Thumbnail = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '135px',

  '& img': {
    position: 'absolute',
    objectFit: 'cover',
    width: '100%',
    height: '100%'
  },

  [theme.breakpoints.up('md')]: {
    height: '212px'
  }
}));

const ThumbnailOverlay = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.focus90.light,
  opacity: '0.15',
  position: 'absolute',
  width: '100%',
  height: '100%'
}));

const ThumbnailPlayIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderRadius: '32px',
  position: 'absolute',

  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',

  opacity: '0.8',
  backgroundColor: theme.palette.focus90.light,

  width: '41px',
  height: '41px',

  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    width: '64px',
    height: '64px'
  }
}));

const PlayTriangle = styled(PlayArrowIcon)(({ theme }) => ({
  color: theme.palette.focus00.light,
  width: '100%',
  height: '26px',

  [theme.breakpoints.up('md')]: {
    height: '36px'
  }
}));

const TimeStampIcon = styled(SharedAsset)({
  height: '14px',
  alignSelf: 'center'
});

const LessonCard = ({
  video
}) => {
  const [showVideo, setShowVideo] = useState(false);
  const [videoDuration, setVideoDuration] = useState('0:00');
  const { thumbnail, title } = content[video];

  const trackCardClick = useButtonPressedCallback({
    ...MT_DATA_TRACKING,
    elementName: 'expert_help_video_card',
    elementValue: video
  });

  const handleClick = (e) => {
    trackCardClick(e);
    setShowVideo((prev) => !prev);
  };

  return (
    <StyledCard>
      {/* Video Dialog */}
      <VideoContentView
        video={video}
        showVideo={showVideo}
        setShowVideo={setShowVideo}
        setVideoDuration={setVideoDuration}
      />

      <StledCardActionArea
        onClick={handleClick}
      >
        <StyledCardMedia
          component="img"
          alt={title}
          image={thumbnail}
        />
        <Thumbnail>
          <ThumbnailOverlay />
          <ThumbnailPlayIcon>
            <PlayTriangle />
          </ThumbnailPlayIcon>
        </Thumbnail>
        <CardBody>
          <CardTitle>
            {title}
          </CardTitle>
          <TimeStamp>
            <TimeStampIcon src="/s2/icon/clock-outline.svg" height={14} width={14} />
            {videoDuration}
          </TimeStamp>
        </CardBody>
      </StledCardActionArea>
    </StyledCard>
  );
};

LessonCard.propTypes = {
  video: PropTypes.string.isRequired
};

export default LessonCard;
